import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Globe2, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  FINANCIAL_STATUS_BANDS,
  formatAnnualRange,
  resolveFinancialStatus,
} from '@/lib/financialStatusBands';

interface FinancialStatusInsightProps {
  /** Annual income in USD to place on the ladder. */
  annualIncome: number;
  /**
   * What the income represents, e.g. "The median salary for Registered
   * Nurses in Texas" or "Your salary". Rendered mid-sentence.
   */
  contextLabel?: string;
  className?: string;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Math.round(value));

// Collapse millionaire+ rows for typical OEWS salaries. Show through the
// Wealthy band ($300K–$1M); collapse when income sits below Rich ($144K–$300K).
const RICH_INDEX = FINANCIAL_STATUS_BANDS.findIndex((b) => b.id === 'rich');
const WEALTHY_INDEX = FINANCIAL_STATUS_BANDS.findIndex((b) => b.id === 'wealthy');

/**
 * Educational "income reality check": places an annual salary on an
 * illustrative financial-status ladder with approximate US and global
 * population shares. Complements (does not replace) OEWS occupation
 * percentiles and RPP purchasing-power insights.
 */
const FinancialStatusInsight = ({ annualIncome, contextLabel = 'This income', className = '' }: FinancialStatusInsightProps) => {
  const [showAllTiers, setShowAllTiers] = useState(false);
  const result = resolveFinancialStatus(annualIncome);

  if (!result) return null;

  const { band, index } = result;
  const collapseUpperTiers = index < RICH_INDEX && !showAllTiers;
  const visibleBands = collapseUpperTiers
    ? FINANCIAL_STATUS_BANDS.slice(0, WEALTHY_INDEX + 1)
    : FINANCIAL_STATUS_BANDS;
  const hiddenCount = FINANCIAL_STATUS_BANDS.length - (WEALTHY_INDEX + 1);

  return (
    <Card className={`border-wage-accent/30 bg-wage-accent/5 ${className}`}>
      <CardContent>
        <div className="flex items-center gap-2 mb-1">
          <div className="p-1.5 rounded-md bg-wage-accent/10 shrink-0">
            <Globe2 className="h-4 w-4 text-wage-accent" />
          </div>
          <h3 className="font-semibold">Income reality check</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          {contextLabel} of <strong>{formatCurrency(annualIncome)}</strong> falls in the{' '}
          <strong>{band.label}</strong> band of this framework - roughly{' '}
          <strong>{band.usaPopShare}</strong> of the U.S. population and{' '}
          <strong>{band.globalPopShare}</strong> of the global population sit in this band.
        </p>

        {/* Ladder. CSS grid (not a table) so column widths are
            deterministic at narrow widths - labels wrap by word while
            numeric columns keep their intrinsic size. */}
        <div className="overflow-hidden rounded-lg border border-wage-accent/20 bg-background/60">
          <div
            className="grid grid-cols-[minmax(0,1fr)_auto_auto] sm:grid-cols-[minmax(0,1fr)_auto_auto_auto] text-[11px] leading-snug sm:text-sm"
            role="table"
            aria-label="Financial status ladder"
          >
            <div role="row" className="contents text-muted-foreground">
              <span role="columnheader" className="py-2 pl-3 pr-2 font-medium">Status</span>
              <span role="columnheader" className="py-2 px-2 text-right font-medium">Annual</span>
              <span role="columnheader" className="py-2 pl-2 pr-3 text-right font-medium">US</span>
              <span role="columnheader" className="hidden sm:block py-2 pl-2 pr-3 text-right font-medium">Global</span>
            </div>
            {visibleBands.map((tier, i) => {
              const active = i === index;
              const cellBase = 'border-t border-border/40 self-stretch flex items-center py-1.5';
              const cellTone = active
                ? 'bg-wage-accent/15 font-semibold text-foreground'
                : 'text-muted-foreground';
              return (
                <div key={tier.id} role="row" className="contents">
                  <span
                    role="cell"
                    className={cn(
                      cellBase,
                      cellTone,
                      'pl-3 pr-2',
                      active && 'border-l-2 border-l-wage-accent',
                    )}
                  >
                    {tier.label}
                  </span>
                  <span role="cell" className={cn(cellBase, cellTone, 'px-2 justify-end text-right tabular-nums whitespace-nowrap')}>
                    {formatAnnualRange(tier)}
                  </span>
                  <span role="cell" className={cn(cellBase, cellTone, 'pl-2 pr-3 justify-end text-right tabular-nums whitespace-nowrap')}>
                    {tier.usaPopShare}
                  </span>
                  <span role="cell" className={cn(cellBase, cellTone, 'hidden sm:flex pl-2 pr-3 justify-end text-right tabular-nums whitespace-nowrap')}>
                    {tier.globalPopShare}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {index < WEALTHY_INDEX && (
          <button
            type="button"
            onClick={() => setShowAllTiers((v) => !v)}
            className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-wage-accent hover:underline underline-offset-2"
          >
            {collapseUpperTiers ? (
              <>
                Show {hiddenCount} higher tiers (millionaire and beyond)
                <ChevronDown className="h-3.5 w-3.5" />
              </>
            ) : (
              <>
                Hide higher tiers
                <ChevronUp className="h-3.5 w-3.5" />
              </>
            )}
          </button>
        )}

        <p className="text-xs text-muted-foreground mt-3">
          Illustrative relative standing for education only - this ladder is a simplified
          framework, not an official Census, BLS, or World Bank statistical series. Salary
          figures on Wage Atlas come from official BLS OEWS data.
        </p>
      </CardContent>
    </Card>
  );
};

export default FinancialStatusInsight;
